import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";

import { Observable } from "rxjs";
import { UserDetailedListItem } from "../models/users/user-detailed-list-item.model";
import { UserListItem } from "../models/users/user-list-item.model";
import { User, UserWithComanyRoles } from "../models/users/user.model";
import { UserCompanyRole } from "../models/access-management/user-company-role.model";
import { UsersFilter } from "../models/users/users-filter.model";
import { map } from "rxjs/operators";
import { Policy } from "../models/access-management/policy.model";
import { ApplicationSettings } from "lib-config";
import { CreateUserWithCompanyAccess } from '../models/access-management/create-user-company-access.model';
import { GrantUserAccess } from '../models/access-management/grant-user-access.model';
import { ManagedUser } from '../models/access-management/managed-user.model';

interface RolesAndPermissionsApi {
  email: string;
  firstName: string;
  lastName: string;
  application: any;
}

interface UserDetails {
  email: string;
  firstName: string;
  lastName: string;
  status: "Active" | "Inactive" | null;
  roles: UserRole[];
  companies: UserCompanyDetails[];
}

interface UserRole {
  id: number;
  name: string;
}

interface UserCompanyDetails {
  policyId: number;
  name: string;
  status: "Active" | "Inactive" | null;
  roles: UserRole[];
  createdBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(private http: HttpClient, private settings: ApplicationSettings) {
  }

  getDetailedUserList(policyId: number, skip: number, take: number, usersFilter: UsersFilter): Observable<UserDetailedListItem[]> {
    let params: HttpParams = new HttpParams();
    if(policyId) params = params.set('PolicyId', policyId.toString());
    if(skip) params = params.set('Skip', skip.toString());
    if(take) params = params.set('Take', take.toString());
    if(usersFilter.email) params = params.set('Email', usersFilter.email);
    if(usersFilter.firstName) params = params.set('FirstName', usersFilter.firstName);
    if(usersFilter.lastName) params = params.set('LastName', usersFilter.lastName);
    if(usersFilter.applicationRole) params = params.set('Role', usersFilter.applicationRole);
    if(usersFilter.lastUpdated) params = params.set('LastUpdated', usersFilter.lastUpdated.toDateString());
    if(usersFilter.lastUpdatedBy) params = params.set('LastUpdatedBy', usersFilter.lastUpdatedBy);
    if(usersFilter.dateOfDeactivation) params = params.set('DateOfDeactivation', usersFilter.dateOfDeactivation.toDateString());
    if(usersFilter.status) params = params.set('Status', usersFilter.status);
    if(usersFilter.includeInactiveUsers) params = params.set('IncludeInactiveUsers', usersFilter.includeInactiveUsers.toString());

    if(usersFilter.sortField?.field) params = params.set('SortField', usersFilter.sortField.field);
    if(usersFilter.sortField?.isDescending) params = params.set('SortDirectionIsDescending', usersFilter.sortField.isDescending.toString());
    
    return this.http.get<Policy>(`${this.settings.backendUrlPolicyService}/manage/User/overview`, {params: params}).pipe(map(x => {
      return x.users?.map(y =>{
        let user = new UserDetailedListItem();
        user.id = y.id;
        user.email = y.email;
        user.firstName = y.firstName;
        user.lastName = y.lastName;
        user.applicationRoleName = y.roles[0]?.name;
        user.applicationRole = y.roles[0]?.id;
        user.lastUpdated = new Date(y.lastUpdated);
        user.lastUpdatedBy = y.lastUpdatedBy;
        user.dateOfDeactivation = y.dateOfDeactivation ? new Date(y.dateOfDeactivation): null;
        user.status = y.status;
        return user;
      } 
    )}));
  }

  getUserList(policyId: number, includeInactiveUsers: boolean, filter: string): Observable<UserListItem[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('PolicyId', policyId.toString());
    params = params.set('IncludeInactiveUsers', includeInactiveUsers.toString());
    if(filter) params = params.set('DataFilter', filter);
    return this.http.get<Policy>(`${this.settings.backendUrlPolicyService}/manage/User/list`, {params: params}).pipe(map(x => {
      return x.users?.map(y =>{
        let user = new UserListItem();
        user.id = y.id;
        user.mail = y.email;
        user.firstName = y.firstName;
        user.surName = y.lastName;
        return user;
      }
    )}));
  }

  getUserListFromDirectory(filter: string): Observable<UserListItem[]> {
    return this.http.get<UserListItem[]>(`${this.settings.backendUrlMasterData}/aadusers/startswith/${filter}`);
  }

  getUser(userName: string): Observable<UserWithComanyRoles> {
    return this.http.get<UserWithComanyRoles>('assets/dummy-json/user.json');
  }

  getCompanyRole(userName: string, companyId: string) : Observable<UserCompanyRole> { 
    return this.http.get<UserCompanyRole>('assets/dummy-json/user-company-role.json');
  }

  createOrUpdateUserRoles(userRole: ManagedUser) {
    return this.http.post(`${this.settings.backendUrlPolicyService}/manage/User`, userRole);
  }

  createBasicUser(userRole: ManagedUser) {
    return this.http.post(`${this.settings.backendUrlMasterData}/user/basic`, userRole);
  }

  grantUserAccess(grantUserAccess: GrantUserAccess) {
    return this.http.post(`${this.settings.backendUrlMasterData}/user/grant-access`, grantUserAccess);
  }

  CreateUserWithCompanyAccess(createUserWithCompanyAccess: CreateUserWithCompanyAccess) {
    return this.http.post(`${this.settings.backendUrlMasterData}/user/with-company-access`, createUserWithCompanyAccess);
  }

  updateUser(user: User) {
    return this.http.put("dummy", user);
  }

  acceptUser(userName: string) {
    return this.http.post("dummy", userName);
  }

  rejectUser(userName: string) {
    return this.http.post("dummy", userName);
  }

  getUserDetails(email: string): Observable<UserDetails>{
    return this.http.get<UserDetails>(this.settings.backendUrlMasterData + `/User/Details/${this.settings.policyServiceAppId}/${email}`)
  }

  getUserRoles(email: string) {
    let params: HttpParams = new HttpParams();
    params = params.set('IncludePermissionGroups', "false");
    params = params.set('IncludePermissions', "false");
    return this.http.get<RolesAndPermissionsApi>(this.settings.backendUrlPolicyService + `/manage/User/${this.settings.policyServiceAppId}/${email}`, {params: params})
  }

  getUserRolesWithPermissions(email: string) {
    return this.http.get<RolesAndPermissionsApi>(this.settings.backendUrlPolicyService + `/manage/User/${this.settings.policyServiceAppId}/${email}`)
  }

  activateUser(email: string) {
    return this.http.put(`${this.settings.backendUrlPolicyService}/manage/User/Status/${this.settings.policyServiceAppId}/${email}/Active`, null)
  }
  activateUserRole(id: number) {
    return this.http.put(`${this.settings.backendUrlPolicyService}/manage/User/Status/${id}/Active`, null)
  }

  deactivateUser(email: string) {
    return this.http.put(`${this.settings.backendUrlPolicyService}/manage/User/Status/${this.settings.policyServiceAppId}/${email}/Inactive`, null)
  }
  deactivateUserRole(id: number) {
    return this.http.put(`${this.settings.backendUrlPolicyService}/manage/User/Status/${id}/Inactive`, null)
  }
}
